import { inject, Injectable } from "@angular/core";
import { first, map } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { GraphqlMessageInterface } from "@app/kernel/graphql/interfaces/graphql-message.interface";
import { IReservationSourceType } from "../interfaces/reservation-source-type.interface";
import { RESERVATION_SOURCE_TYPE_FORM_QUERY } from "../queries/reservation-source-type-form.query";

/**
 * GraphQL operations for reservation source types. Provided on the domain route, not in root.
 * Create and update go through the form mutation configs.
 */
@Injectable()
export class ReservationSourceTypeApiService {
  /** GraphQL service used for executing queries and mutations. */
  private readonly graphService = inject(GraphService);

  /**
   * Fetches a reservation source type by id or entityId.
   *
   * @param id - The reservation source type id or entityId.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the reservation source type.
   * @throws GraphQL error if the response contains errors.
   */
  getReservationSourceType(id: string, dataShape: string | string[] = RESERVATION_SOURCE_TYPE_FORM_QUERY) {
    return this.graphService.constructQuery<{ getReservationSourceType: IReservationSourceType }>(dataShape, "getReservationSourceType", { id }).pipe(
      first(),
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data.getReservationSourceType;
      })
    );
  }

  /**
   * Enables a reservation source type.
   *
   * @param id - The reservation source type id.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the enabled reservation source type.
   * @throws GraphQL error if the response contains errors.
   */
  enableReservationSourceType(id: string, dataShape: string | string[] = RESERVATION_SOURCE_TYPE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{ enableReservationSourceType: IReservationSourceType }>("enableReservationSourceType", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.enableReservationSourceType;
        })
      );
  }

  /**
   * Disables a reservation source type.
   *
   * @param id - The reservation source type id.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the disabled reservation source type.
   * @throws GraphQL error if the response contains errors.
   */
  disableReservationSourceType(id: string, dataShape: string | string[] = RESERVATION_SOURCE_TYPE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{ disableReservationSourceType: IReservationSourceType }>("disableReservationSourceType", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.disableReservationSourceType;
        })
      );
  }

  /**
   * Soft-deletes a reservation source type.
   *
   * @param id - The reservation source type id.
   * @returns An observable that emits the backend message.
   * @throws GraphQL error if the response contains errors.
   */
  deleteReservationSourceType(id: string) {
    return this.graphService
      .constructMutation<{
        deleteReservationSourceType: GraphqlMessageInterface;
      }>("deleteReservationSourceType", { id: "ID!" }, { id }, ["text", "code"])
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.deleteReservationSourceType;
        })
      );
  }
}
